import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FieldRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Button } from '@mui/material';
import imageCompression from 'browser-image-compression';
import cn from 'classnames';
import heic2any from 'heic2any';

import ImageSlickCarousel from '@components/ImageMultiCarousel/ImageMultiCarousel';
import Loader from '@components/Loader/Loader';
import { fileUploadProgressSelector } from '@store/features/fileUploadState/selectors';
import {
  clearFileUploadErrors,
  setFilesUploadInProgress,
  setFileUploadErrors,
} from '@store/features/fileUploadState/slice';
import { handleGetUserSupplementSettings } from '@store/features/supplementSettings/actions';
import useDispatch from '@store/hooks/useDispatch';
import useSupplementsImageSettings from '@store/hooks/useSupplementsImageSettings';

import './styles.scss';

interface IProps extends FieldRenderProps<string> {
  className?: string;
  responsiveImagesSettings?: any[];
  slidesToShow?: number;
}

interface IFileEntry {
  file: File;
  preview: string;
}

enum ErrorCodes {
  FileTooLarge = 'file-too-large',
  WrongType = 'file-invalid-type',
  ProcessingError = 'failed-to-process',
}

const BYTES_IN_MB = 1024 * 1024;

const FileUploadField: FC<IProps> = ({
  className,
  input,
  responsiveImagesSettings = undefined,
  ...other
}) => {
  const [uploadedImages, setUploadedImages] = useState<IFileEntry[]>([]);
  const [fileProcessingErrors, setFileProcessingErrors] = useState<string[]>([]);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLInputElement | null>(null);
  const { allowedFileExtensionsInquiryUpload, maxFileSizeInquiryUploadMb } =
    useSupplementsImageSettings();

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const uploadingInProgress = useSelector(fileUploadProgressSelector);

  const maxFileSizeAllowedInBytes = useMemo(() => {
    if (!maxFileSizeInquiryUploadMb) {
      return 0;
    }
    return maxFileSizeInquiryUploadMb * BYTES_IN_MB;
  }, [maxFileSizeInquiryUploadMb]);

  const hintText = useMemo(() => {
    if (!allowedFileExtensionsInquiryUpload.length) {
      return null;
    }
    return `${t('common.use')} ${allowedFileExtensionsInquiryUpload
      .filter(item => !item.includes('heif'))
      .join(', ')}`;
  }, [allowedFileExtensionsInquiryUpload]);

  const notifyAboutProcessingFiles = useCallback((isInProgress: boolean) => {
    dispatch(setFilesUploadInProgress(isInProgress));
  }, []);

  const createErrorMessage = useCallback(
    (fileName: string, errorCode: string): string => {
      if (errorCode === ErrorCodes.FileTooLarge) {
        return `${t('common.file')} ${fileName} ${t(
          'errors.isLargerThan',
        )} ${maxFileSizeInquiryUploadMb} Mb`;
      }
      if (errorCode === ErrorCodes.WrongType) {
        return `${t('common.file')} ${fileName} ${t(
          'errors.fileTypeError',
        )} ${allowedFileExtensionsInquiryUpload.join(', ')}`;
      }
      if (errorCode === ErrorCodes.ProcessingError) {
        return `${t('common.file')} ${fileName} ${t('errors.convertOrCompressError')}`;
      }
      return '';
    },
    [t, maxFileSizeInquiryUploadMb, allowedFileExtensionsInquiryUpload],
  );

  const onDropHandler = useCallback(
    async (acceptedFiles: File[]) => {
      if (uploadingInProgress || !acceptedFiles.length) {
        return;
      }
      setFileProcessingErrors([]);
      notifyAboutProcessingFiles(true);
      const filesFailedToProcess = [] as string[];
      let files = [...acceptedFiles] as (File | null)[];
      const processedFiles = [] as IFileEntry[];
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < files.length; i++) {
        let result = files[i] as File;
        try {
          if (result.type === 'image/heic' || result.type === 'image/heif') {
            // eslint-disable-next-line no-await-in-loop
            let convertedBlob: any = await heic2any({
              blob: result,
              toType: 'image/jpeg',
              quality: 0.5,
            });
            const fileNameArr = result.name.split('.');
            const nameWithNewExt = `${fileNameArr.slice(0, fileNameArr.length - 1).join('.')}.jpeg`;
            result = new File([convertedBlob as Blob], nameWithNewExt, {
              type: 'image/jpeg',
              lastModified: result.lastModified,
            });
            convertedBlob = null;
          }
          if (result.size > BYTES_IN_MB) {
            const options = {
              maxSizeMB: 1,
              useWebWorker: true,
            };
            // eslint-disable-next-line no-await-in-loop
            result = await imageCompression(result, options);
          }
          result = new File([result as Blob], result.name, {
            type: result.type,
            lastModified: result.lastModified,
          });
          files[i] = null;
          if (result.size > maxFileSizeAllowedInBytes) {
            filesFailedToProcess.push(createErrorMessage(result.name, ErrorCodes.FileTooLarge));
            // eslint-disable-next-line no-continue
            continue;
          }
          processedFiles.push({
            file: result,
            preview: URL.createObjectURL(result),
          });
        } catch (error) {
          filesFailedToProcess.push(createErrorMessage(result.name, ErrorCodes.ProcessingError));
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      files = [];
      setUploadedImages(prevState => [
        ...prevState.map(item => ({ ...item, preview: URL.createObjectURL(item.file) })),
        ...(processedFiles.filter(item => !!item) as IFileEntry[]),
      ]);
      setFileProcessingErrors(filesFailedToProcess);
      notifyAboutProcessingFiles(false);
    },
    [
      createErrorMessage,
      uploadingInProgress,
      inputRef,
      maxFileSizeAllowedInBytes,
      notifyAboutProcessingFiles,
    ],
  );

  const handleDeleteImage = useCallback(
    (imageIndex: number, event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      const filteredFiles = uploadedImages.reduce((prev, item, index) => {
        if (index !== imageIndex) {
          prev.push({
            file: item.file,
            preview: URL.createObjectURL(item.file),
          });
        }
        return prev;
      }, [] as IFileEntry[]);
      setUploadedImages(filteredFiles);
    },
    [uploadedImages],
  );

  const handleUploadButtonClick = useCallback(() => {
    if (uploadingInProgress) {
      return;
    }
    inputRef.current?.click();
  }, [uploadingInProgress, inputRef]);

  const dropzoneAccept: Record<string, []> = useMemo(() => {
    if (
      Array.isArray(allowedFileExtensionsInquiryUpload) &&
      allowedFileExtensionsInquiryUpload.length
    ) {
      return allowedFileExtensionsInquiryUpload.reduce((prev: Record<string, []>, item) => {
        const mimeType = `image/${item.substring(1)}`;
        prev[mimeType] = [];
        return prev;
      }, {});
    }
    return {};
  }, [allowedFileExtensionsInquiryUpload]);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop: onDropHandler,
    accept: dropzoneAccept,
    maxSize: maxFileSizeAllowedInBytes * 3,
    disabled: uploadingInProgress,
    multiple: true,
  });

  useEffect(() => {
    dispatch(handleGetUserSupplementSettings());
  }, [dispatch]);

  useEffect(() => {
    input.onChange(uploadedImages.map(item => item.file));
    return () => {
      uploadedImages.forEach(image => URL.revokeObjectURL(image.preview));
      input.onChange([]);
    };
  }, [uploadedImages]);

  useEffect(() => {
    const errors = fileRejections.reduce((prev, error) => {
      const errorCodes = new Set(error.errors.map(errorMeta => errorMeta.code));
      if (errorCodes.has(ErrorCodes.FileTooLarge)) {
        prev.push(createErrorMessage(error.file.name, ErrorCodes.FileTooLarge));
      }
      if (errorCodes.has(ErrorCodes.WrongType)) {
        prev.push(createErrorMessage(error.file.name, ErrorCodes.WrongType));
      }
      return prev;
    }, [] as string[]);
    if (fileProcessingErrors.length) {
      fileProcessingErrors.forEach(errorMessage => errors.push(errorMessage));
    }
    dispatch(setFileUploadErrors(errors));

    return () => {
      dispatch(clearFileUploadErrors());
    };
  }, [fileRejections, fileProcessingErrors]);

  return (
    <div
      className={cn(
        'form-file-upload',
        isDragActive ? 'form-file-upload__is-drag-active' : '',
        className,
      )}
      {...other}
    >
      <div
        {...getRootProps()}
        onClick={e => e.preventDefault()}
        className="form-file-upload__dropzone"
      >
        <input {...getInputProps()} ref={inputRef} />
        <div
          className={cn('form-file-upload__container', {
            'form-file-upload__container--uploaded': !!uploadedImages.length || uploadingInProgress,
          })}
        >
          {!uploadedImages.length && !uploadingInProgress && (
            <>
              <Box component="img" src="/images/img-file-upload.svg" />
              <p>{t('common.dragAndDropFilesHere')}</p>
              <p>{t('common.or')}</p>
            </>
          )}
          <Button
            onClick={handleUploadButtonClick}
            disabled={uploadingInProgress}
            variant="outlined"
            color="primary"
            component="span"
            className="form-file-upload__browse-button"
          >
            {t('common.browse')}
          </Button>
          {hintText && <Box className="form-file-upload__hint">{hintText}</Box>}
          {uploadingInProgress && <Loader className="form-file-upload__loader" />}
          {!!uploadedImages.length && !uploadingInProgress && (
            <Box className="form-file-upload__images-holder" ref={containerRef}>
              <ImageSlickCarousel responsive={responsiveImagesSettings} containerRef={containerRef}>
                {uploadedImages.map((image, index) => (
                  <Box key={image.preview} className="form-file-upload__slide-holder">
                    <Box className="form-file-upload__slide">
                      <Box
                        component="img"
                        src={image.preview}
                        alt={image.file.name}
                        width={100}
                        height={100}
                      />
                      <Box className="form-file-upload__image-name-container">
                        <p className="form-file-upload__image-name">{image.file.name}</p>
                      </Box>
                      <span
                        className="form-file-upload__delete-icon"
                        onClick={e => handleDeleteImage(index, e)}
                      >
                        <Box
                          component="img"
                          src="/images/img-file-delete.svg"
                          width={16}
                          height={16}
                        />
                      </span>
                    </Box>
                  </Box>
                ))}
              </ImageSlickCarousel>
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};
export default FileUploadField;
